import React, { useState, useEffect } from 'react';
import { X, Sparkles, CheckCircle, FileText, Copy, Download, Loader, AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const TranscriptionModal = ({
    isOpen,
    onClose,
    job,
    onCopyToClipboard
}) => {
    const [showTimestamps, setShowTimestamps] = useState(true);

    if (!isOpen) return null;

    const isProcessing = job?.status === 'processing';
    const isCompleted = job?.status === 'completed';
    const isFailed = job?.status === 'failed';
    const progress = job?.progress || 0;

    const removeTimestamps = (text) => {
        if (!text) return '';
        // Matches [00:00], [00:00:00], (00:00), 00:00 etc, plus optional trailing " - " or " " 
        return text.replace(/\[?\d{1,2}:\d{2}(:\d{2})?\]?(\s*-\s*|\s+)?/g, '');
    };

    const finalTranscript = isCompleted && job?.transcript
        ? (showTimestamps ? job.transcript : removeTimestamps(job.transcript))
        : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md animate-fade-in">
            <div
                className="bg-white w-full max-w-2xl max-h-[85vh] rounded-[40px] shadow-2xl overflow-hidden flex flex-col relative animate-pop-in"
                onClick={e => e.stopPropagation()}
            >
                {/* --- HEADER --- */}
                <div className="flex items-center justify-between p-6 pb-4 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-2xl ${isCompleted ? 'bg-green-100 text-green-600' : 'bg-indigo-100 text-indigo-600'}`}>
                            {isCompleted ? <CheckCircle size={24} /> : <Sparkles size={24} className={isProcessing ? "animate-pulse" : ""} />}
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-gray-900 tracking-tight font-['Outfit']">
                                {isCompleted ? "Script Ready!" : isFailed ? "Ingest Failed" : "Studio Ingest"}
                            </h2>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                {isCompleted ? "Transcription Complete" : isFailed ? "Error Encountered" : "Processing Audio..."}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-900 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* --- CONTENT AREA --- */}
                <div className="flex-1 overflow-hidden relative flex flex-col">

                    {/* PROCESSING STATE: ANIMATIONS */}
                    {isProcessing && (
                        <div className="flex-1 flex flex-col items-center justify-center p-10 text-center relative overflow-hidden">

                            {/* Animated Background Blobs */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-200/50 rounded-full blur-3xl animate-pulse"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-purple-200/50 rounded-full blur-2xl animate-spin-slow"></div>

                            <div className="relative z-10 mb-8">
                                <div className="w-32 h-32 relative">
                                    <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
                                        <circle cx="50" cy="50" r="45" fill="none" stroke="#e0e7ff" strokeWidth="8" />
                                        <circle cx="50" cy="50" r="45" fill="none" stroke="#6366f1" strokeWidth="8" strokeDasharray="283" strokeDashoffset={283 - (283 * progress / 100)} strokeLinecap="round" className="transition-all duration-500 ease-out" />
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-2xl font-black text-indigo-600">{Math.round(progress)}%</span>
                                    </div>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-gray-800 mb-2 relative z-10">Transcribing Magic Happening...</h3>
                            <p className="text-sm text-gray-500 max-w-xs mx-auto relative z-10">
                                Our AI is listening to every word and formatting your script. Hang tight!
                            </p>
                        </div>
                    )}

                    {/* FAILED STATE */}
                    {isFailed && (
                        <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-4 animate-bounce">
                                <AlertTriangle size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Oops! Something went wrong.</h3>
                            <p className="text-sm text-gray-500 max-w-xs mx-auto mb-6">
                                We couldn't process this file. Please ensure it's a valid audio/video format and try again.
                            </p>
                            <button onClick={onClose} className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors">
                                Close & Retry
                            </button>
                        </div>
                    )}

                    {/* COMPLETED STATE: SCRIPT VIEW */}
                    {isCompleted && job.transcript && (
                        <div className="flex-1 flex flex-col min-h-0 bg-gray-50/50">
                            <div className="flex-1 overflow-y-auto p-8 prose prose-indigo max-w-none">
                                <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 min-h-full">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {finalTranscript}
                                    </ReactMarkdown>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="p-6 bg-white border-t border-gray-100 flex items-center justify-between gap-4 z-20 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)]">
                                <div
                                    onClick={() => setShowTimestamps(!showTimestamps)}
                                    className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                                >
                                    <div className={`w-10 h-6 flex items-center rounded-full p-1 duration-300 ease-in-out ${showTimestamps ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                                        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${showTimestamps ? 'translate-x-4' : ''}`}></div>
                                    </div>
                                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Timestamps</span>
                                </div>
                                <button
                                    onClick={() => onCopyToClipboard(finalTranscript)}
                                    className="px-5 py-3 bg-white border border-gray-200 hover:border-indigo-300 text-gray-600 hover:text-indigo-600 font-bold rounded-xl transition-all shadow-sm active:scale-95 flex items-center gap-2 text-sm"
                                >
                                    <Copy size={16} /> Copy Text
                                </button>
                                <button
                                    onClick={onClose}
                                    className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 active:scale-95 transition-all text-sm flex items-center gap-2"
                                >
                                    <CheckCircle size={18} /> Save to Episode
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TranscriptionModal;
